import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { formatCurrency } from '../utils/formatters';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { DayPicker } from 'react-day-picker';

function CreditCardsPage() {
    const [activeTab, setActiveTab] = useState('cards');

    // --- CARDS STATE ---
    const [cards, setCards] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({ name: '', limit: '', closing_day: '', due_day: '' });
    const [editingId, setEditingId] = useState(null);

    // --- SPLIT BILL STATE ---
    const [splitForm, setSplitForm] = useState({
        cardName: '', totalAmount: '', splitAmount: '', personName: ''
    });
    const [dueDate, setDueDate] = useState(new Date());
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [isSplitLoading, setIsSplitLoading] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        fetchCards();
    }, []);

    const fetchCards = async () => {
        try {
            const response = await api.get('/credit-cards/');
            setCards(response.data);
        } catch (error) {
            toast.error('Erro ao buscar cartões.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = (card = null) => {
        if (card) {
            setEditingId(card.id);
            setFormData({
                name: card.name,
                limit: card.limit,
                closing_day: card.closing_day,
                due_day: card.due_day
            });
        } else {
            setEditingId(null);
            setFormData({ name: '', limit: '', closing_day: '', due_day: '' });
        }
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja remover este cartão?')) {
            try {
                await api.delete(`/credit-cards/${id}`);
                toast.success('Cartão removido!');
                fetchCards();
            } catch (error) {
                toast.error('Erro ao deletar.');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await api.put(`/credit-cards/${editingId}`, formData);
                toast.success('Cartão atualizado!');
            } else {
                await api.post('/credit-cards/', formData);
                toast.success('Cartão criado!');
            }
            setIsModalOpen(false);
            fetchCards();
        } catch (error) {
            toast.error('Erro ao salvar cartão.');
        }
    };

    // --- SPLIT BILL LOGIC ---
    const handleSplitSubmit = async (e) => {
        e.preventDefault();
        const { cardName, totalAmount, splitAmount, personName } = splitForm;
        if (!cardName || !totalAmount || !dueDate || !splitAmount || !personName) {
            toast.error('Por favor, preencha todos os campos.');
            return;
        }
        setIsSplitLoading(true);
        try {
            await api.post('/transactions/', {
                title: `Fatura Cartão ${cardName}`,
                amount: parseFloat(totalAmount),
                date: format(dueDate, 'y-MM-dd'),
                type: 'EXPENSE',
                category: 'Fatura de Cartão',
            });
            await api.post('/transactions/', {
                title: `Divisão Fatura ${cardName} - ${personName}`,
                amount: parseFloat(splitAmount),
                date: format(dueDate, 'y-MM-dd'),
                type: 'INCOME',
                category: 'Reembolso',
            });
            toast.success('Fatura dividida lançada com sucesso!');
            setSplitForm({ cardName: '', totalAmount: '', splitAmount: '', personName: '' });
            setDueDate(new Date());
        } catch (error) {
            toast.error('Erro ao lançar fatura dividida.');
        } finally {
            setIsSplitLoading(false);
        }
    };

    return (
        <>
            <div className="mb-8">
                <h2 className="text-4xl font-bold font-manrope text-slate-900 tracking-tight mb-2">Cartões de Crédito</h2>
                <p className="text-sm font-medium text-slate-500">Gerencie seus cartões corporativos, acompanhe limites e gerencie divisões de faturas.</p>
            </div>

            {/* TABS */}
            <div className="flex space-x-8 border-b border-slate-200 mb-8">
                <button
                    onClick={() => setActiveTab('cards')}
                    className={`pb-4 text-sm font-bold transition-colors relative flex items-center space-x-2 ${activeTab === 'cards' ? 'text-primary' : 'text-slate-500 hover:text-slate-800'}`}
                >
                    <span className="material-symbols-outlined text-[20px]">credit_card</span>
                    <span>Meus Cartões</span>
                    {activeTab === 'cards' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full"></div>}
                </button>
                <button
                    onClick={() => setActiveTab('split')}
                    className={`pb-4 text-sm font-bold transition-colors relative flex items-center space-x-2 ${activeTab === 'split' ? 'text-primary' : 'text-slate-500 hover:text-slate-800'}`}
                >
                    <span className="material-symbols-outlined text-[20px]">call_split</span>
                    <span>Dividir Fatura</span>
                    {activeTab === 'split' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full"></div>}
                </button>
            </div>

            {activeTab === 'cards' && (
                <div className="flex justify-between items-end mb-8">
                    <div>
                        <h3 className="text-xl font-bold font-manrope text-slate-900">Seus Cartões</h3>
                        <p className="text-sm font-medium text-slate-500 mt-1">Total de cartões cadastrados: {cards.length}</p>
                    </div>
                    <button onClick={() => handleOpenModal()} className="flex items-center space-x-2 bg-primary text-white px-4 py-2.5 rounded-lg font-bold text-sm shadow-md hover:bg-indigo-700 transition-colors">
                        <span className="material-symbols-outlined text-lg">add</span>
                        <span>Novo Cartão</span>
                    </button>
                </div>
            )}

            {activeTab === 'cards' && isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <ClipLoader color="#3525cd" size={50} />
                </div>
            ) : activeTab === 'cards' && cards.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-100 p-16 flex flex-col items-center justify-center text-center shadow-sm">
                    <span className="material-symbols-outlined text-6xl text-slate-200 mb-4">credit_card_off</span>
                    <h4 className="text-lg font-bold text-slate-700 mb-2">Nenhum cartão cadastrado</h4>
                    <p className="text-sm text-slate-500 mb-6">Cadastre seu primeiro cartão de crédito corporativo para gerenciar limites.</p>
                    <button onClick={() => handleOpenModal()} className="text-primary font-bold text-sm hover:underline">Adicionar Primeiro Cartão</button>
                </div>
            ) : activeTab === 'cards' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {cards.map((card, index) => {
                        const colors = [
                            'bg-slate-900',
                            'bg-slate-800',
                            'bg-[#1a1c29]'
                        ];
                        const bgColor = colors[index % colors.length];
                        return (
                            <div key={card.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg transition-all group flex flex-col">
                                <div className={`${bgColor} p-6 text-white relative overflow-hidden`} style={{ height: '220px' }}>
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-10 -mt-10 blur-xl"></div>
                                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full -ml-8 -mb-8 blur-md"></div>

                                    <div className="flex justify-between items-start mb-8 relative z-10">
                                        <h3 className="text-lg font-bold tracking-widest uppercase opacity-90">{card.name}</h3>
                                        <span className="material-symbols-outlined opacity-70 border border-white/20 p-1 rounded">sim_card</span>
                                    </div>

                                    <div className="mb-6 relative z-10">
                                        <p className="font-mono text-xl tracking-[0.2em] opacity-80">•••• •••• •••• {card.id.toString().slice(-4).padEnd(4, '0')}</p>
                                    </div>

                                    <div className="flex justify-between items-end relative z-10">
                                        <div>
                                            <p className="text-[10px] uppercase tracking-widest opacity-60 mb-1">Limite Total</p>
                                            <p className="font-bold text-lg">{formatCurrency(parseFloat(card.limit))}</p>
                                        </div>
                                        <div className="flex space-x-1 opacity-80">
                                            <div className="w-6 h-6 rounded-full bg-red-500/80 mix-blend-screen"></div>
                                            <div className="w-6 h-6 rounded-full bg-yellow-500/80 mix-blend-screen -ml-3"></div>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-6 bg-white flex-1 flex flex-col justify-between">
                                    <div className="space-y-4 mb-6">
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-500 font-medium">Dia Fechamento</span>
                                            <span className="font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded">Dia {card.closing_day}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-500 font-medium">Dia Vencimento</span>
                                            <span className="font-bold text-primary bg-indigo-50 px-2 py-0.5 rounded">Dia {card.due_day}</span>
                                        </div>
                                    </div>

                                    <div className="flex space-x-3 mt-auto pt-4 border-t border-slate-100">
                                        <button onClick={() => navigate(`/app/credit-cards/${card.id}`)} className="flex-1 bg-slate-50 hover:bg-slate-100 text-slate-700 font-bold text-sm py-2 rounded-lg transition-colors border border-slate-200">
                                            Faturas
                                        </button>
                                        <button onClick={() => handleOpenModal(card)} className="w-10 h-10 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors" title="Editar">
                                            <span className="material-symbols-outlined text-[18px]">edit</span>
                                        </button>
                                        <button onClick={() => handleDelete(card.id)} className="w-10 h-10 flex items-center justify-center rounded-lg border border-red-100 bg-red-50 text-error hover:bg-red-100 transition-colors" title="Excluir">
                                            <span className="material-symbols-outlined text-[18px]">delete</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            ) : null}

            {activeTab === 'split' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
                        <div className="flex items-center space-x-3 mb-6">
                            <div className="w-10 h-10 rounded-lg bg-indigo-50 text-primary flex items-center justify-center">
                                <span className="material-symbols-outlined">call_split</span>
                            </div>
                            <div>
                                <h3 className="text-xl font-bold font-manrope text-slate-900">Gerar Divisão</h3>
                                <p className="text-sm font-medium text-slate-500">Lançamento duplo de fatura e reembolso.</p>
                            </div>
                        </div>

                        <form className="space-y-5" onSubmit={handleSplitSubmit}>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Cartão de Origem</label>
                                <input type="text" value={splitForm.cardName} onChange={(e) => setSplitForm({ ...splitForm, cardName: e.target.value })} placeholder="Nome do Cartão (ex: Nubank)" required className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm font-medium text-slate-800 outline-none focus:ring-2 focus:ring-primary" />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Valor Total da Fatura (R$)</label>
                                <input type="number" step="0.01" value={splitForm.totalAmount} onChange={(e) => setSplitForm({ ...splitForm, totalAmount: e.target.value })} placeholder="0.00" required className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm font-medium text-slate-800 outline-none focus:ring-2 focus:ring-primary" />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Vencimento da Fatura</label>
                                <div className="relative">
                                    <button type="button" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm font-medium text-slate-800 outline-none focus:ring-2 focus:ring-primary text-left flex items-center justify-between" onClick={() => setIsPickerOpen(!isPickerOpen)}>
                                        <span>{format(dueDate, 'dd/MM/yyyy', { locale: ptBR })}</span>
                                        <span className="material-symbols-outlined text-slate-400 text-sm">calendar_today</span>
                                    </button>
                                    {isPickerOpen && (
                                        <div className="absolute top-12 left-0 z-50 bg-white shadow-xl rounded-xl border border-slate-200 p-2">
                                            <DayPicker mode="single" selected={dueDate} onSelect={(date) => { if (date) setDueDate(date); setIsPickerOpen(false); }} locale={ptBR} />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="my-6 border-b border-dashed border-slate-200"></div>

                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Terceiro Envolvido</label>
                                <input type="text" value={splitForm.personName} onChange={(e) => setSplitForm({ ...splitForm, personName: e.target.value })} placeholder="Nome ou Empresa" required className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm font-medium text-slate-800 outline-none focus:ring-2 focus:ring-primary" />
                            </div>
                            <div className="mb-6">
                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Valor da Parte Deles (R$)</label>
                                <input type="number" step="0.01" value={splitForm.splitAmount} onChange={(e) => setSplitForm({ ...splitForm, splitAmount: e.target.value })} placeholder="0.00" required className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm font-medium text-slate-800 outline-none focus:ring-2 focus:ring-primary" />
                            </div>

                            <button type="submit" disabled={isSplitLoading} className="w-full bg-primary text-white font-bold py-3.5 px-4 rounded-xl shadow-md hover:bg-indigo-700 transition-colors disabled:opacity-50">
                                {isSplitLoading ? 'Lançando...' : 'Processar Valores'}
                            </button>
                        </form>
                    </div>

                    <div className="bg-slate-50 rounded-xl border border-slate-200 p-8 flex flex-col justify-center text-center">
                        <div className="w-20 h-20 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center mx-auto mb-6">
                            <span className="material-symbols-outlined text-4xl text-slate-400">sync_alt</span>
                        </div>
                        <h3 className="text-xl font-bold font-manrope text-slate-900 mb-4">Mecânica de Divisão</h3>
                        <p className="text-sm text-slate-500 font-medium leading-relaxed mb-8 max-w-sm mx-auto">
                            Esta ferramenta lança dois registros simultâneos de forma automatizada para manter seu balanço alinhado perfeitamente.
                        </p>
                        <div className="flex flex-col gap-3 max-w-sm mx-auto w-full">
                            <div className="flex items-center space-x-3 p-3 bg-red-50 text-error rounded-lg text-sm font-bold border border-red-100">
                                <span className="material-symbols-outlined text-lg">arrow_downward</span>
                                <span className="flex-1 text-left">Despesa: Total da Fatura</span>
                            </div>
                            <div className="flex items-center space-x-3 p-3 bg-[#f0fdf4] text-[#16a34a] rounded-lg text-sm font-bold border border-[#dcfce7]">
                                <span className="material-symbols-outlined text-lg">arrow_upward</span>
                                <span className="flex-1 text-left">Receita: Parte Reembolsada</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}>
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                            <h3 className="text-xl font-bold font-manrope text-slate-900">{editingId ? 'Editar Cartão' : 'Novo Cartão'}</h3>
                            <button className="text-slate-400 hover:text-slate-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors" onClick={() => setIsModalOpen(false)}><span className="material-symbols-outlined text-[20px]">close</span></button>
                        </div>
                        <div className="p-6">
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Nome do Cartão</label>
                                    <input required type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ex: Corporate Black" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm font-medium text-slate-800 outline-none focus:ring-2 focus:ring-primary" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Limite Total (R$)</label>
                                    <input required type="number" step="0.01" value={formData.limit} onChange={(e) => setFormData({ ...formData, limit: e.target.value })} placeholder="0.00" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm font-medium text-slate-800 outline-none focus:ring-2 focus:ring-primary" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Dia Fechamento</label>
                                        <input required min="1" max="31" type="number" value={formData.closing_day} onChange={(e) => setFormData({ ...formData, closing_day: e.target.value })} placeholder="1-31" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm font-medium text-slate-800 outline-none focus:ring-2 focus:ring-primary" />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Dia Vencimento</label>
                                        <input required min="1" max="31" type="number" value={formData.due_day} onChange={(e) => setFormData({ ...formData, due_day: e.target.value })} placeholder="1-31" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm font-medium text-slate-800 outline-none focus:ring-2 focus:ring-primary" />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
                                    <button type="button" className="px-6 py-2.5 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-100 transition-colors" onClick={() => setIsModalOpen(false)}>Cancelar</button>
                                    <button type="submit" className="px-6 py-2.5 rounded-lg text-sm font-bold bg-primary text-white hover:bg-indigo-700 shadow-md transition-colors">{editingId ? 'Atualizar' : 'Salvar Cartão'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default CreditCardsPage;